import { Group, Select, SelectProps, Text } from '@mantine/core';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import DeviceTypeIcon from 'components/device/DeviceTypeIcon';
import { forwardRef, useEffect, useMemo } from 'react';
import { translateRevisionPeriod } from 'components/inputs/revision-period-select/RevisionPeriodSelect';

type RevisionPlans = RevisionGetResponse['devices'][number]['revisionPlan'];

type RevisionTermSelectProps = Omit<SelectProps, 'data'> & {
  options: RevisionPlans;
  autoSelectSingleResult?: boolean;
  withIcons?: boolean;
};

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  image: string;
  label: string;
}

const RevisionTermItem = forwardRef<HTMLDivElement, ItemProps>(({ image, label, ...others }: ItemProps, ref) => {
  return (
    <div ref={ref} {...others}>
      <Group noWrap>
        {image && <DeviceTypeIcon typeId={Number(image)} />}
        <Text size="sm">{label}</Text>
      </Group>
    </div>
  );
});

/**
 * Select for revision term of a device.
 */
export default function RevisionTermSelect({
  options,
  autoSelectSingleResult = false,
  withIcons = false,
  onChange,
  ...props
}: RevisionTermSelectProps) {
  const data = useMemo(
    () =>
      (options || []).map((rp) => ({
        label: `${rp.description} (${translateRevisionPeriod(rp.revisionPeriod)})`,
        value: String(rp.revisionPlanId),
        image: rp.deviceTypeId && withIcons ? String(rp.deviceTypeId) : '',
      })),
    [options, withIcons]
  );

  useEffect(() => {
    if (autoSelectSingleResult && data.length === 1) {
      onChange?.(data[0].value);
    }
  }, [autoSelectSingleResult, data.length]);

  return <Select searchable data={data} onChange={onChange} itemComponent={RevisionTermItem} {...props} />;
}
